/**
 * The Android navigation bar, held away while a screen wants the whole height.
 *
 * Only the camera screen asks. The page is edge-to-edge in the Android shell,
 * so the bar is a strip of three system buttons drawn over the bottom of the
 * viewfinder, under the shutter — the one thing a phone's own camera never
 * shows. A call hides the same bar for the same reason, from its own plugin
 * (see lib/callAudio.ts); this is the camera's way to the same window, and the
 * native side (NavigationBar.kt) keeps the two from undoing each other.
 *
 * Nothing changes on the page: `--safe-area-inset-bottom` follows the bar to
 * zero and back, and the camera's bottom padding follows it. iOS has no such
 * bar and no plugin, and the browser has neither — both are the no-op below.
 */
import { registerPlugin } from '@capacitor/core';

/** The plugin's name on both sides — `name` in NavigationBarPlugin.kt. */
export const NAVIGATION_BAR_PLUGIN = 'NavigationBar';

interface NavigationBarPlugin {
  hide: () => Promise<void>;
  show: () => Promise<void>;
}

const NavigationBar = registerPlugin<NavigationBarPlugin>(NAVIGATION_BAR_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * True only inside the Android app, and only once the plugin is registered
 * there — a web bundle ahead of its shell takes the no-op, as every plugin here
 * does.
 */
export const hasNavigationBar: boolean = (() => {
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  if (capacitor?.getPlatform?.() !== 'android') return false;
  return capacitor?.isPluginAvailable?.(NAVIGATION_BAR_PLUGIN) === true;
})();

/**
 * Hides the bar until the returned function is called. Synchronous either way,
 * so it can be an effect's body: the cleanup runs when the screen goes, not
 * when a promise happens to settle.
 */
export function holdNavigationBar(): () => void {
  if (!hasNavigationBar) return () => {};
  // Failures are swallowed on both legs: a bar that stayed is a smaller thing
  // than a message over the viewfinder, and there is nothing to retry.
  void NavigationBar.hide().catch(() => {});
  return () => {
    void NavigationBar.show().catch(() => {});
  };
}
